import { snakeCaseToTitleCase } from "common/utils/strings";
import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_95: DocMigration = {
  maxSchemaVersion: "0.0.95",
  description: "SettingsSets",
  apply(json: any) {
    // 1. fix settings with incorrect types
    function migrateSettingsWithIncorrectType(settingsOverride) {
      if (settingsOverride?.rearPorch) {
        if (json.settings[settingsOverride.rearPorch]) {
          json.settings[settingsOverride.rearPorch].type = "REAR_PORCH_SETTINGS";
        }
      }
      if (settingsOverride?.porch) {
        if (json.settings[settingsOverride.porch]) {
          json.settings[settingsOverride.porch].type = "PORCH_SETTINGS";
        }
      }
      if (settingsOverride?.exteriorDoorCasing) {
        if (json.settings[settingsOverride.exteriorDoorCasing]) {
          json.settings[settingsOverride.exteriorDoorCasing].type = "EXTERIOR_DOOR_CASING_SETTINGS";
        }
      }
      if (settingsOverride?.electrical) {
        if (json.settings[settingsOverride.electrical]) {
          json.settings[settingsOverride.electrical].type = "ELECTRICAL_SETTINGS";
        }
      }
    }

    migrateSettingsWithIncorrectType(json.settingsOverride);
    Object.values(json.elevations).forEach((e) => migrateSettingsWithIncorrectType((e as any).settingsOverride));
    Object.values(json.rooms).forEach((e) => migrateSettingsWithIncorrectType((e as any).settingsOverride));

    // 2. Add base settings override and migrate document level settingsOverride to that
    const baseSettingsSetId = uid();
    json.settingsSets = {};
    json.settingsSets[baseSettingsSetId] = {
      entityType: "SETTINGS_SET",
      name: "Base Settings Set",
      id: baseSettingsSetId,
      sortIndex: 0,
    };

    let extMtlCount = 0;
    let intMtlCount = 0;

    function migrateSettingsOverrideToParentSettingsSet(
      settingsOverride = {},
      parentId: string,
      elevationName?: string,
    ) {
      for (const settingsType in settingsOverride) {
        const settingsId = settingsOverride[settingsType];
        const settings = json.settings[settingsId];

        if (settings) {
          // create a new settings id to fix a bug
          // where some settings have multiple room parents
          let newSettingsId: string;

          switch (settings.type) {
            case "EXTERIOR_MATERIAL_SETTINGS":
              newSettingsId = `extmtl${extMtlCount++}`;
              break;
            case "INTERIOR_MATERIAL_SETTINGS":
              newSettingsId = `intmtl${intMtlCount++}`;
              break;
            default:
              newSettingsId = uid();
              break;
          }

          json.settings[newSettingsId] = {
            ...settings,
            entityType: "SETTINGS",
            id: newSettingsId,
            parentId,
            sortIndex: 0,
          };

          if (elevationName && json.settings[newSettingsId].type === "EXTERIOR_MATERIAL_SETTINGS") {
            json.settings[newSettingsId].paramName = "Exterior Palette for " + elevationName;
          }

          // some old settings have the wrong type name
          if (json.settings[newSettingsId].type === "EXTERIOR_DOOR_HEAD_CASING_SETTINGS") {
            json.settings[newSettingsId].type = "EXTERIOR_DOOR_CASING_SETTINGS";
            json.settings[newSettingsId].name = snakeCaseToTitleCase(settings.type);
          }
        }
      }
    }

    function deleteSettingsFromSettingsOverride(settingsOverride = {}) {
      for (const settingsType in settingsOverride) {
        const settingsId = settingsOverride[settingsType];
        delete json.settings[settingsId];
      }
    }

    migrateSettingsOverrideToParentSettingsSet(json.settingsOverride, baseSettingsSetId);
    deleteSettingsFromSettingsOverride(json.settingsOverride);
    delete json.settingsOverride;

    let settingsSetCount = 1;

    // 3. Add settings set for each elevation and migrate settings to be children of that
    for (const elevationId in json.elevations) {
      const elevation = json.elevations[elevationId];

      const settingsSetId = uid();

      json.settingsSets[settingsSetId] = {
        entityType: "SETTINGS_SET",
        name: "Settings Set for " + elevation.prettyName,
        id: settingsSetId,
        sortIndex: settingsSetCount++,
        disabled: {
          type: "FORMULA",
          formula: `not (elevationStyle == "${elevationId}")`,
        },
      };

      migrateSettingsOverrideToParentSettingsSet(elevation.settingsOverride, settingsSetId, elevation.prettyName);
    }

    for (const elevationId in json.elevations) {
      const elevation = json.elevations[elevationId];
      deleteSettingsFromSettingsOverride(elevation.settingsOverride);
      delete elevation.settingsOverride;
    }

    // 4. remove settingsOverrides from rooms, update parentId to roomId
    for (const roomId in json.rooms) {
      const room = json.rooms[roomId];
      migrateSettingsOverrideToParentSettingsSet(room.settingsOverride, roomId);
    }

    for (const roomId in json.rooms) {
      const room = json.rooms[roomId];
      deleteSettingsFromSettingsOverride(room.settingsOverride);
      delete room.settingsOverride;
    }

    // delete orphaned settings
    for (const settingsId of Object.keys(json.settings)) {
      const parentId = json.settings[settingsId].parentId;
      if (!json.rooms[parentId] && !json.settingsSets[parentId]) {
        delete json.settings[parentId];
      }
    }

    return json;
  },
};

export default migration_0_0_95;
